import { Injectable } from '@angular/core';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import 'firebase/compat/auth';
import 'firebase/compat/storage';
import { environment } from '../../environments/environment';
import { emulatorPort } from '../shared/utils/emulator-port';

/** The named Firebase app instance the whole tracker talks to (OE-188 PRD). Every
 *  environment points this at the SAME central project (`newgenus-issues`) — that's the
 *  whole point of the central store. */
const ISSUES_APP_NAME = 'issues';

function getOrCreateIssuesApp(): firebase.app.App {
    const existing = firebase.apps.find((a) => a?.name === ISSUES_APP_NAME);
    if (existing) return existing;

    const app = firebase.initializeApp(environment.issuesFirebase, ISSUES_APP_NAME);

    // Mirrors how the DEFAULT app is wired to the local emulators (app.module.ts) — same
    // host/port choice (isDockerized), just applied directly to the named app's SDK instances
    // instead of through @angular/fire's USE_*_EMULATOR injection tokens (there's no DI
    // hook for a second named app).
    if (environment.useEmulators) {
        const host = environment.isDockerized ? 'emulator' : 'localhost';
        app.firestore().useEmulator(host, emulatorPort(8080));
        app.auth().useEmulator(`http://${host}:${emulatorPort(9099)}/`);
        app.storage().useEmulator(host, emulatorPort(9199));
    }

    return app;
}

/**
 * Creates (once) and exposes the named "issues" Firebase app + its Firestore/Auth/Storage
 * instances, all compat SDK — `IssuesService` already worked at the compat Firestore boundary
 * for most calls, so pointing it at this app instead of the default one is mechanical.
 */
@Injectable({ providedIn: 'root' })
export class IssuesFirebase {
    public readonly app: firebase.app.App = getOrCreateIssuesApp();
    public readonly firestore: firebase.firestore.Firestore = this.app.firestore();
    public readonly auth: firebase.auth.Auth = this.app.auth();
    public readonly storage: firebase.storage.Storage = this.app.storage();
}
